import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of, switchMap, take } from 'rxjs';
import { UserService } from '../user/user.service';
import { BrokerProfileService } from './broker-profile.service';
import { ManagementEntityType } from '@core/enums/management-entity-type.enum';
import { User } from '../user/user.interface';

@Injectable({
    providedIn: 'root'
})
export class BrokerProfileGuard implements CanActivate, CanActivateChild {

    private _userCache: User | null = null;

    constructor(
        private _userService: UserService,
        private _brokerProfileService: BrokerProfileService,
        private _router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        return this._check();
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        // Use cached result for child routes
        if (this._userCache) {
            return of(this._evaluateAccess(this._userCache));
        }
        return this._check();
    }

    private _check(): Observable<boolean | UrlTree> {
        // Fetch user directly - guards run before resolvers so user$ may not be populated yet
        return this._userService.get().pipe(
            take(1),
            switchMap((user) => {
                this._userCache = user;
                return of(this._evaluateAccess(user));
            })
        );
    }

    private _evaluateAccess(user: User): boolean | UrlTree {
        // If no user or user is not a broker, allow access
        if (!user || !user.id || user.entityAccessLevel !== ManagementEntityType.BROKER_POINT_OF_SALE) {
            return true;
        }

        // If broker has selected a profile, allow access
        if (this._brokerProfileService.hasSelectedProfile()) {
            return true;
        }

        // Broker without selected profile, redirect to select-profile
        return this._router.createUrlTree(['/select-profile']);
    }
}
